import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, Leaf, Heart, Brain, Wind, Moon } from "lucide-react";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { Button } from "@/components/ui/button";
import { CLINIC } from "@/lib/brand";

const heroBg = "https://static.prod-images.emergentagent.com/jobs/b5c830c1-97ad-4ff3-85a5-a88855f921e5/images/e66717799b32022cb991fbad69ca43fa8b0fe15a3f628556c4a06a37f75f4a4d.png";
const doctorImg = "https://images.pexels.com/photos/7108327/pexels-photo-7108327.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const medicineImg = "https://images.unsplash.com/photo-1725267882596-2d08e560b250?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxob21lb3BhdGh5JTIwbmF0dXJhbCUyMG1lZGljaW5lfGVufDB8fHx8MTc3OTcyNjE5MHww&ixlib=rb-4.1.0&q=85";

const conditions = [
  { icon: Brain, title: "Mental wellness", desc: "Anxiety, stress, sleep & focus issues" },
  { icon: Wind, title: "Respiratory", desc: "Asthma, allergies, sinusitis & cough" },
  { icon: Heart, title: "Chronic conditions", desc: "Migraine, PCOS, thyroid & arthritis" },
  { icon: Moon, title: "Sleep & lifestyle", desc: "Insomnia, fatigue, hormonal balance" },
  { icon: Leaf, title: "Skin & hair", desc: "Eczema, psoriasis, acne & hair fall" },
  { icon: Sparkles, title: "Paediatric care", desc: "Gentle remedies for children" },
];

export default function Landing() {
  return (
    <div className="min-h-screen">
      <MarketingHeader />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <img src={heroBg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-white/55 to-[color:var(--secondary-light)]/30" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-[color:var(--border)] text-xs font-medium text-[color:var(--primary-hover)] mb-6">
              <Sparkles className="w-3.5 h-3.5" /> {CLINIC.tagline}
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-[color:var(--ink)] leading-[1.02]">
              Gentle, personal,<br />
              <span className="text-[color:var(--secondary)] italic font-light">deeply natural</span> healing.
            </h1>
            <p className="mt-6 text-base md:text-lg text-[color:var(--muted)] max-w-xl leading-relaxed">
              {CLINIC.name} offers individualised classical homeopathic consultations — for chronic
              conditions, mental wellness and family care — across India and online.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book" data-testid="hero-book-btn">
                <Button className="rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white px-7 h-12 text-[15px]">
                  Book consultation <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/about" data-testid="hero-about-btn">
                <Button variant="outline" className="rounded-full border-[color:var(--secondary)] text-[color:var(--secondary)] hover:bg-[color:var(--secondary-light)]/40 hover:text-[color:var(--secondary)] px-6 h-12 text-[15px]">
                  Meet the doctor
                </Button>
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <Stat n="12+" label="years of practice" />
              <Stat n="5,000+" label="patients treated" />
              <Stat n="4.9★" label="patient rating" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border border-white/40 fade-up">
              <img src={doctorImg} alt="Consulting at the clinic" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-white border border-[color:var(--border)] rounded-2xl px-5 py-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[color:var(--primary-light)] grid place-items-center text-[color:var(--primary-hover)]">
                  <ShieldCheck className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[color:var(--ink)]">Registered Homoeopath</p>
                  <p className="text-xs text-[color:var(--muted)]">{CLINIC.doctorTitle} — 12+ yrs</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions */}
      <section className="py-20 md:py-28 bg-[color:var(--bg-marketing)]">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-7">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[color:var(--muted)] mb-3">What we treat</p>
              <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight">
                Care that listens to the whole person.
              </h2>
            </div>
            <p className="md:col-span-5 text-[color:var(--muted)] text-base leading-relaxed">
              Every prescription is built from a deep case-history. We combine classical homeopathic
              principles with modern follow-up to bring sustainable relief.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {conditions.map((c) => (
              <div key={c.title} className="bg-white border border-[color:var(--border)] rounded-2xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300" data-testid={`condition-card-${c.title.toLowerCase().replace(/\s/g,'-')}`}>
                <div className="w-11 h-11 rounded-xl bg-[color:var(--secondary-light)]/50 grid place-items-center text-[color:var(--secondary)] mb-4">
                  <c.icon className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <h3 className="font-heading text-xl font-medium text-[color:var(--ink)]">{c.title}</h3>
                <p className="text-sm text-[color:var(--muted)] mt-2 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Method */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-square rounded-3xl overflow-hidden">
              <img src={medicineImg} alt="Homeopathy medicine" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[color:var(--muted)] mb-3">Our approach</p>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mb-6">A method, not a moment.</h2>
            <div className="space-y-6">
              {[
                { n: "01", t: "Deep case-taking", d: "60–90 min consultation covering mind, body, history and miasms." },
                { n: "02", t: "Individualised remedy", d: "Single classical prescription with potency suited to your constitution." },
                { n: "03", t: "Guided follow-ups", d: "Structured 4–6 week reviews to track symptom shift and refine care." },
              ].map(s => (
                <div key={s.n} className="flex gap-5">
                  <span className="font-heading text-2xl text-[color:var(--secondary)] font-light">{s.n}</span>
                  <div>
                    <h4 className="font-heading text-lg font-medium text-[color:var(--ink)]">{s.t}</h4>
                    <p className="text-sm text-[color:var(--muted)] leading-relaxed">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-[color:var(--secondary-light)]/40 via-[color:var(--lavender-light)]/30 to-[color:var(--primary-light)]/40">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-center mb-12">Trusted by families across India</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { q: "Dr Harleen helped me come off chronic migraine medication in six months. Calm, patient, deeply knowledgeable.", a: "Anita R., Delhi" },
              { q: "My daughter's eczema cleared without steroids. We finally feel heard.", a: "Vikram J., Mumbai" },
              { q: "Honest, methodical and never rushed. Online consultations are seamless.", a: "Sneha I., Bengaluru" },
            ].map((t) => (
              <figure key={t.a} className="bg-white rounded-2xl p-7 border border-white" data-testid={`testimonial-${t.a.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                <blockquote className="text-[15px] text-[color:var(--ink)] leading-relaxed italic">"{t.q}"</blockquote>
                <figcaption className="mt-5 text-xs font-semibold tracking-[0.18em] uppercase text-[color:var(--muted)]">— {t.a}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-5 md:px-10 text-center fade-up">
          <h2 className="font-heading text-4xl md:text-6xl font-light tracking-tight">Ready to begin your healing journey?</h2>
          <p className="mt-5 text-[color:var(--muted)] text-base max-w-2xl mx-auto">
            Book a consultation today — in person at our Mumbai clinic, or online from anywhere in India.
          </p>
          <Link to="/book" className="inline-block mt-8" data-testid="cta-book-btn">
            <Button className="rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white px-8 h-12 text-[15px]">
              Book your consultation
            </Button>
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <p className="font-heading text-3xl font-light text-[color:var(--ink)]">{n}</p>
      <p className="text-xs text-[color:var(--muted)] tracking-wide">{label}</p>
    </div>
  );
}
